import { useMemo } from 'react';
import { useSticky } from './sticky';

/**
 * 메뉴·탭 순서를 계정마다 바꿔 둔다(2026-10-08 게시판 #160 — 「사용자 편의에 따라 메뉴 순서를 커스텀으로」).
 *
 * 저장은 id 목록 하나(`user_pref` — 다른 PC 에서도 같다). 화면에 실제로 보일 항목은 늘 **기본 목록**이 정한다:
 * - 저장값에 있는데 지금 없는 id(권한으로 숨음·메뉴 삭제)는 건너뛴다 — 권한이 돌아오면 그 자리로 돌아온다.
 * - 저장값에 없는 id(나중에 생긴 메뉴)는 기본 순서상 **바로 앞 메뉴 뒤**에 끼운다 — 맨 끝으로 밀려 숨지 않게.
 */
const isIds = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

export function applyOrder<T>(items: T[], order: string[] | null, idOf: (t: T) => string): T[] {
  if (!order || order.length === 0) return items;
  const byId = new Map(items.map((t) => [idOf(t), t] as const));
  const out: T[] = order.filter((id) => byId.has(id)).map((id) => byId.get(id)!);
  const placed = new Set(out.map(idOf));
  items.forEach((t, i) => {
    if (placed.has(idOf(t))) return;
    // 기본 순서에서 앞쪽으로 거슬러 올라가 이미 놓인 메뉴를 찾고 그 뒤에 넣는다(없으면 맨 앞).
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = out.findIndex((o) => idOf(o) === idOf(items[j]!));
      if (k >= 0) { at = k + 1; break; }
    }
    out.splice(at, 0, t);
    placed.add(idOf(t));
  });
  return out;
}

/** `[정렬된 목록, 저장값, 저장 함수]` — 저장 함수에 `null` 을 주면 기본 순서로 돌아간다. */
export function useMenuOrder<T>(key: string, items: T[], idOf: (t: T) => string) {
  const [order, setOrder] = useSticky<string[] | null>(key, null, { persist: true, valid: isIds });
  // idOf 는 호출부마다 새 함수라 의존성에서 뺀다(같은 항목은 같은 id 를 낸다).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sorted = useMemo(() => applyOrder(items, order, idOf), [items, order]);
  return [sorted, order, setOrder] as const;
}
